import { useMemo } from 'react'
import { Monitor, Volume2 } from 'lucide-react'
import { useAuthStore } from '@/store/auth'
import { useGuildStore } from '@/store/guilds'
import { useVoiceStore } from '@/store/voice'
import { P, channelPermissions, has } from '@/lib/permissions'
import { cx } from '@/lib/cx'
import styles from './GuildRail.module.css'

/**
 * Значок в правом верхнем углу сервера в колонке: кто-то в голосе — динамик, кто-то стримит —
 * монитор. Зелёный — это ваш сервер: вы там в голосе (динамик) или смотрите стрим (монитор).
 * Считаются только каналы, которые вам видны (иначе значок выдавал бы скрытые каналы), и без
 * ботов — музыкальный бот сам по себе серверу «живости» не добавляет.
 */
export function GuildActivity({ guildId }: { guildId: string }) {
  const guild = useGuildStore((s) => s.guilds[guildId])
  const channels = useGuildStore((s) => s.channels[guildId])
  const states = useGuildStore((s) => s.voiceStates[guildId])
  const members = useGuildStore((s) => s.members[guildId])
  const meId = useAuthStore((s) => s.user?.id)
  const mine = useVoiceStore((s) => s.connection?.guildId === guildId)
  // watching сохраняется при переподключении, но значок требует живой публикации экрана.
  const watching = useVoiceStore((s) => s.connection?.guildId === guildId && Object.keys(s.watching).some((id) => id in s.live))

  const activity = useMemo(() => {
    let voice = false
    let live = false
    if (!guild || !meId || !states) return { voice, live }
    const viewer = (members?.[meId]) ?? { user: { id: meId }, roles: [] }
    const seen = new Map<string, boolean>()
    const visible = (channelId: string) => {
      let ok = seen.get(channelId)
      if (ok === undefined) {
        const ch = channels?.[channelId]
        ok = Boolean(ch && has(channelPermissions(guild, viewer, ch), P.VIEW_CHANNEL))
        seen.set(channelId, ok)
      }
      return ok
    }
    for (const vs of Object.values(states)) {
      if (!vs.channel_id || !visible(vs.channel_id)) continue
      if ((members?.[vs.user_id] ?? vs.member)?.user.bot) continue
      voice = true
      if (vs.self_stream) live = true
    }
    return { voice, live }
  }, [guild, channels, states, members, meId])

  if (!mine && !activity.voice) return null
  const screen = mine ? watching : activity.live
  return (
    <span className={cx(styles.activity, mine && styles.activityMine)} aria-hidden="true">
      {screen ? <Monitor size={12} strokeWidth={2.5} /> : <Volume2 size={12} strokeWidth={2.5} />}
    </span>
  )
}
